// スキル：キャラ別ツリー＋解放力（GAME_DESIGN.md 15章）。
// - 主Lvでスキルが解放される（習得費なし）。キャラごとに、装着したスキルのコスト合計 ≤ 解放力。
// - 解放力 = base + 主Lv × perLevel ＋上乗せ（式神・里の道場・外伝クリア。合計 bonusMax まで）。4人それぞれが満額使える。
// - 4人の専用ツリー（16スキル、コスト3〜10）＋全員共通の基本スキル（身体・戦術・建築、コスト1〜3）。
// - おすすめ構成（autoBuild）は優先度順の貪欲法。想定育成（推奨戦力）もこれを使う。
import { styleOf } from './styles';
import { GRAPH_CORE, graphNeighbors, graphReach, skillGraph } from './skill-graph';
import { LINE_JUTSU_LV, LINE_OUGI_LV, LINE_MAX, WAZA_LVS, isLineToken, linePoints, lineCost, lineEffects, lineLevels, lineOpenWaza, lineToken, skillLines } from './skill-lines';
import { LADDER_STATS, ladderAt, ladderLinked, ladderOpen, ladderX, skillLadder } from './skill-ladder';
import { JUTSU, JUTSU_BY_ID, defaultJutsu } from '../data/jutsu';
import type { BuildingType, CharId } from '../types';
import { CHAR_IDS } from '../types';
import { CONFIG, configVersion } from './config';
import { levelTier } from './level';
import { addMod, DISCRETE_KEYS, type Effect, type ModKey, type Mods } from './mods';
import type { OpResult, Profile } from './profile';

/** 建物の解放の順（先頭の BUILD_START 種＝櫓・竹柵・幟は最初から）。共通の「図面」とスズネの「鬼の図面」の数だけ続きが使える */
export const BUILD_ORDER: readonly BuildingType[] = ['yagura', 'sakaku', 'nobori', 'otoshiana', 'shuzen', 'torii', 'kitsunebi', 'wara', 'horoku', 'ozutsu', 'teppo', 'toseki', 'raiko', 'dokutsubo', 'hiya'];
/** 最初から建てられる種類の数（建築はバトルの柱なので、序盤から櫓・竹柵・幟で組み立てられるように） */
export const BUILD_START = 3;

/** char = 専用ツリー、body/tactics/build = 共通（身体・戦術・建築） */
export type SkillGroup = 'char' | 'body' | 'tactics' | 'build';
export const SKILL_GROUPS: readonly SkillGroup[] = ['char', 'body', 'tactics', 'build'];
export const SKILL_GROUP_LABEL: Record<SkillGroup, string> = { char: '専用', body: '身体', tactics: '戦術', build: '建築' };

export interface SkillNode {
  id: string;
  /** null = 全員共通 */
  charId: CharId | null;
  group: SkillGroup;
  /** 専用ツリー：列（系統 0..3）。共通：行（同じ系統の段の並び） */
  col: number;
  /** 専用ツリー：段（0..3、上から）。共通：段（0..） */
  tier: number;
  /** 系統の名前（専用の列・共通の行） */
  line: string;
  name: string;
  desc: string;
  cost: number;
  /** この主Lvで解放 */
  unlockLv: number;
  /** 前提（すべて装着していること） */
  requires: string[];
  effects: Effect[];
  /** 戦闘に渡すフラグ（battle の BATTLE_FLAGS・surge:*） */
  flags: string[];
  /** おすすめ構成の優先度（高いほど先に入れる） */
  prio: number;
  /** 妖力を伸ばすスキル */
  surge?: boolean;
  /** 専用ツリーの幹の先に1つだけ突き出る（妖力の奥義） */
  apex?: boolean;
}

const e = (key: ModKey, value: number): Effect => ({ key, value });
const pct = (v: number) => `${Math.round(Math.abs(v) * 1000) / 10}%`;
const sign = (v: number) => (v < 0 ? '-' : '+');

/** 効果の文（説明に使う。数値はここから作るので、効果を変えれば説明も変わる） */
export function effectText(x: Effect): string {
  const v = x.value;
  const P: Partial<Record<ModKey, string>> = {
    hp: 'HP', atk: '攻撃', def: '防御', agi: '速さ', skillPower: '術の威力', splash: '術の範囲ダメージ', core: '本陣への与ダメ', boss: 'ボスへの与ダメ',
    neutral: '中立への与ダメ', taken: '被ダメ', potionHeal: '回復薬の回復', buildHp: '建物HP', buildPower: '建物の効果', buildCost: '建築費', territory: '陣地率',
  };
  if (P[x.key]) return `${P[x.key]}${sign(v)}${pct(v)}`;
  switch (x.key) {
    case 'lifesteal': return `与ダメの${pct(v)}を回復`;
    case 'cd': return `術の待ち手番${v}`;
    case 'move': return `移動${sign(v)}${Math.abs(v)}`;
    case 'range': return `射程${sign(v)}${Math.abs(v)}`;
    case 'unlock': return `建物の種類+${v}`;
    case 'maxCount': return `建物の上限+${v}`;
    case 'perTurn': return '1手番に2回建てられる';
    case 'buildRange': return `建築の射程+${v}`;
    case 'matStart': return `初期陣材+${v}`;
    case 'buildCostFlat': return `建物の陣材${v}（最低1）`;
    case 'matIncome': return `毎手番の陣材+${v}`;
    case 'potionCount': return `回復薬+${v}`;
    case 'respawn': return `復活が${Math.abs(v)}手番早い`;
    case 'diceMin': return `移動の最低歩数+${v}`;
    case 'diceEven': return `丁（賽の合計が偶数）の手番の与ダメ+${pct(v)}`;
    case 'diceOdd': return `半（賽の合計が奇数）の手番の被ダメ${v < 0 ? '+' : '-'}${pct(v)}`;
    case 'diceHigh': return `賽の合計が10以上の手番の与ダメ+${pct(v)}`;
    case 'diceLow': return `賽の合計が9以下の手番の移動+${v}`;
    case 'dicePair': return `同じ目がそろった手番の与ダメ+${pct(v)}`;
    case 'diceRole': return `役が出た手番の与ダメ+${pct(v)}`;
    case 'diceRoleMat': return `役が出ると陣材+${v}`;
    case 'diceSkill': return `術の賽の上がり幅+${pct(v)}`;
    case 'diceLowAtk': return `賽の合計が9以下の手番の与ダメ-${pct(v)}`;
    case 'diceNoRole': return `役が出なかった手番の与ダメ-${pct(v)}`;
    default: return `${x.key}${sign(v)}${v}`;
  }
}
const describe = (note: string, effects: Effect[]) => [note, ...effects.map(effectText)].filter(Boolean).join('・');
const TIER = ['壱', '弐', '参', '肆', '伍', '陸'];

// ---------------- 共通（基本スキル） ----------------

interface ChainSpec {
  id: string;
  group: Exclude<SkillGroup, 'char'>;
  line: string;
  costs: number[];
  lvs: number[];
  prio: number[];
  /** 段 i の効果（1段ぶん） */
  effects: (i: number) => Effect[];
  /** 効果の文の前に付ける説明（建物の名前など） */
  note?: (i: number) => string;
  names?: string[];
  /** 戦闘に渡すフラグ（全段に同じ） */
  flags?: string[];
}

let rowOf: Record<string, number> = {};
function chain(c: ChainSpec): SkillNode[] {
  const row = (rowOf[c.group] = (rowOf[c.group] ?? -1) + 1);
  return c.costs.map((cost, i) => ({
    id: `${c.id}${i + 1}`,
    charId: null,
    group: c.group,
    col: row,
    tier: i,
    line: c.line,
    name: c.names?.[i] ?? `${c.line}・${TIER[i]}`,
    desc: describe(c.note?.(i) ?? '', c.effects(i)),
    cost,
    unlockLv: c.lvs[i],
    requires: i > 0 ? [`${c.id}${i}`] : [],
    effects: c.effects(i),
    flags: c.flags ?? [],
    prio: c.prio[i],
    ...(c.flags?.some((f) => f.startsWith('surge:')) ? { surge: true } : {}),
  }));
}

const COMMON: SkillNode[] = [
  // 身体
  ...chain({ id: 'c_hp', group: 'body', line: '鍛錬', costs: [1, 2, 3, 3, 3], lvs: [1, 82, 217, 397, 577], prio: [9, 7, 6, 5, 4], effects: () => [e('hp', 0.02)] }),
  ...chain({ id: 'c_atk', group: 'body', line: '剛力', costs: [1, 2, 3, 3, 3], lvs: [10, 100, 244, 424, 604], prio: [9, 7, 6, 5, 4], effects: () => [e('atk', 0.02)] }),
  ...chain({ id: 'c_def', group: 'body', line: '鉄壁', costs: [1, 2, 3, 3, 3], lvs: [28, 136, 280, 460, 640], prio: [8, 6, 5, 4, 3], effects: () => [e('def', 0.03)] }),
  ...chain({ id: 'c_agi', group: 'body', line: '身軽', costs: [1, 2, 3, 3], lvs: [64, 190, 370, 550], prio: [7, 5, 4, 3], effects: () => [e('agi', 0.02)] }), // 1.2% → 2%（剛力と同じ。崖なしの CTB で速さは攻撃と同じ効き目。BALANCE.md 38章）
  // 戦術
  ...chain({ id: 'c_core', group: 'tactics', line: '城攻め', costs: [1, 2, 2, 3], lvs: [37, 172, 352, 532], prio: [7, 5, 4, 3], effects: () => [e('core', 0.03)] }),
  ...chain({ id: 'c_neutral', group: 'tactics', line: '妖祓い', costs: [1, 2, 2, 3], lvs: [19, 154, 334, 514], prio: [6, 5, 4, 3], effects: () => [e('neutral', 0.04)] }),
  ...chain({ id: 'c_boss', group: 'tactics', line: '大物狩り', costs: [2, 3, 3], lvs: [262, 487, 712], prio: [4, 3, 2], effects: () => [e('boss', 0.03)] }),
  ...chain({ id: 'c_guard', group: 'tactics', line: '受け流し', costs: [2, 3, 3], lvs: [226, 406, 676], prio: [5, 4, 3], effects: () => [e('taken', -0.015)] }),
  ...chain({
    id: 'c_potion',
    group: 'tactics',
    line: '薬師',
    costs: [1, 2, 3],
    lvs: [55, 307, 442],
    prio: [6, 4, 4],
    names: ['薬師・壱', '薬師・弐', '薬籠'],
    effects: (i) => (i < 2 ? [e('potionHeal', 0.04)] : [e('potionCount', 1)]),
  }),
  ...chain({ id: 'c_terr', group: 'tactics', line: '陣取り', costs: [2, 2, 3], lvs: [181, 379, 613], prio: [5, 4, 3], effects: () => [e('territory', 0.01)] }),
  ...chain({ id: 'c_respawn', group: 'tactics', line: '反魂', costs: [3], lvs: [343], prio: [4], names: ['反魂'], effects: () => [e('respawn', -1)] }),
  // 建築（建物の解放は全員がここで最低限そろえられる。スズネは専用ツリーで大きく伸ばせる）
  ...chain({
    id: 'c_bp',
    group: 'build',
    line: '図面',
    costs: [1, 2, 2, 3],
    lvs: [127, 298, 478, 658],
    prio: [6, 4, 3, 2],
    // 建物は図面（強化画面）の Lv1 で覚える。この系統は建物の強化
    names: ['名工の図面・壱', '名工の図面・弐', '名工の図面・参', '名工の図面・肆'],
    effects: () => [e('buildPower', 0.05), e('buildHp', 0.05)],
  }),
  ...chain({ id: 'c_bhp', group: 'build', line: '頑丈', costs: [1, 2, 2, 3], lvs: [73, 208, 388, 568], prio: [6, 4, 3, 2], effects: () => [e('buildHp', 0.065)] }),
  // 倹約（建築費-3%×4）は削除（2026-10-22）：建物の陣材は2〜9の整数で、割引が四捨五入で消えて実質効かなかった
  ...chain({ id: 'c_bpow', group: 'build', line: '工夫', costs: [1, 2, 3], lvs: [118, 316, 496], prio: [5, 3, 2], effects: () => [e('buildPower', 0.05)] }),
  ...chain({ id: 'c_bmax', group: 'build', line: '縄張り', costs: [2, 3], lvs: [163, 505], prio: [5, 3], effects: () => [e('maxCount', 1)] }),
  ...chain({
    id: 'c_mat',
    group: 'build',
    line: '段取り',
    costs: [1, 2, 3, 5],
    lvs: [109, 289, 559, 739],
    prio: [5, 3, 2, 2],
    // 肆：建物の陣材-1（整数で効く。割合の割引は建物の陣材が2〜9と小さく、四捨五入で消えて効かなかった）
    effects: (i) => (i < 2 ? [e('matStart', 1)] : i === 2 ? [e('matStart', 1), e('matIncome', 1)] : [e('buildCostFlat', -1)]),
  }),
];
rowOf = {};

// ---------------- 専用ツリー ----------------

type CharNode = [
  key: string,
  name: string,
  desc: string,
  cost: number,
  unlockLv: number,
  effects: Effect[],
  flags: string[],
  prio: number,
];

/** 列（系統）ごとに上から4段。各段は1つ上の段が前提 */
function tree(charId: CharId, lines: [string, CharNode[]][], extraReq: Record<string, string[]> = {}): SkillNode[] {
  const out: SkillNode[] = [];
  lines.forEach(([line, nodes], col) =>
    nodes.forEach(([key, name, desc, cost, unlockLv, effects, flags, prio], tier) => {
      const id = `${charId}_${key}`;
      out.push({
        id,
        charId,
        group: 'char',
        col,
        tier,
        line,
        name,
        desc: describe(desc, effects),
        cost,
        unlockLv,
        requires: [...(tier > 0 ? [`${charId}_${nodes[tier - 1][0]}`] : []), ...(extraReq[key] ?? []).map((k) => `${charId}_${k}`)],
        effects,
        flags,
        prio,
        surge: flags.some((f) => f.startsWith('surge:')) || undefined,
      });
    }),
  );
  return out;
}

// 専用ツリーの3系統＝3つの型（BALANCE.md 18〜22章のビルドの検証）。4つ目は妖力を伸ばす覚醒、幹の先に奥義
const AKARI = tree('akari', [
  [
    '花火',
    [
      ['hidane', '火種', '', 3, 1, [e('skillPower', 0.08)], [], 9],
      ['kakusan', '拡散花火', '', 5, 82, [e('splash', 0.165), e('skillPower', 0.04)], [], 7],
      ['bigbloom', '大輪', '', 9, 244, [e('splash', 0.165), e('skillPower', 0.08)], ['ougi:akari_bigbloom'], 6],
      ['hyakka', '百花繚乱', '', 10, 586, [e('cd', -1)], ['ougi:akari_hyakka'], 8],
    ],
  ],
  [
    '陣取り',
    [
      ['toshin', '灯心', '', 3, 19, [e('atk', 0.04)], [], 8],
      ['lantern', '灯し火', '術の着弾点と周りのマスを塗る', 10, 172, [e('territory', 0.025)], ['ougi:akari_lantern'], 8],
      ['gyoretsu', '提灯行列', '自陣の中で与ダメ1.2倍', 9, 352, [e('atk', 0.05)], ['conqueror'], 6],
      ['senka', '千灯万華', '', 10, 694, [e('atk', 0.065), e('skillPower', 0.08)], [], 5],
    ],
  ],
  [
    '守り',
    [
      ['kamihari', '紙張り', '', 3, 37, [e('hp', 0.05)], [], 8],
      ['kekkai', '結界', '', 5, 136, [e('taken', -0.05)], [], 6],
      ['mamori', '灯籠の守り', '自陣の中で防御+25%・手番の最初にHP5%回復', 7, 316, [e('hp', 0.065), e('taken', -0.04)], ['sanctuary'], 6],
      ['joyato', '常夜灯', '手番の最初にHP5%回復', 9, 514, [e('taken', -0.06), e('hp', 0.04)], ['regen'], 5],
    ],
  ],
  [
    '覚醒',
    [
      ['hayazame', '早覚め', '妖力の弐・参に必要な撃破ポイント-1', 5, 100, [], ['surge:fast:-1'], 6],
      ['hanabi', '花火の目覚め', '妖力の弐「花火の輪」の効果1.4倍（術の範囲+25%→+35%）', 4, 208, [e('skillPower', 0.04)], ['surge:boost:akari:2=1.4'], 5],
      ['dairin', '大輪の目覚め', '妖力の参「大輪」の効果1.3倍（術の威力+30%→+39%）。参に上がった瞬間、術がすぐ使える', 7, 406, [], ['surge:boost:akari:3=1.3', 'surge:on3:skill'], 4],
      ['hoshifuri', '星降り', '', 8, 766, [e('atk', 0.05), e('hp', 0.05)], [], 4],
    ],
  ],
]);

const KAZAMARU = tree('kazamaru', [
  [
    '速攻',
    [
      ['eiso', '鋭爪', '', 3, 1, [e('atk', 0.04)], [], 9],
      ['hayagake', '天狗の早駆け', '', 7, 190, [e('atk', 0.08)], ['ougi:kazamaru_hayagake'], 7],
      ['ikioi', '勢い', 'その手番に動いたマス数×2.5%の与ダメ上乗せ（最大+20%）', 10, 352, [], ['stride'], 6],
      ['shinsoku', '神速', '', 10, 532, [e('move', 1)], ['ougi:kazamaru_shinsoku'], 6],
    ],
  ],
  [
    '離脱',
    [
      ['minokonashi', '身のこなし', '', 3, 28, [e('agi', 0.035)], [], 8],
      ['shisso', '疾走', '', 6, 262, [e('agi', 0.05), e('taken', -0.025)], [], 6],
      ['hanare', '天狗の離れ技', '攻撃の後に2マスまで動ける', 9, 442, [], ['hitrun'], 6],
      ['kakure', '天狗隠れ', '', 8, 712, [e('taken', -0.05), e('hp', 0.04)], [], 4],
    ],
  ],
  [
    '狩り',
    [
      ['kazekiri', '風切り', '', 3, 46, [e('skillPower', 0.08), e('atk', 0.02)], [], 8],
      ['senpu', '旋風', '疾風に範囲40%が付き、範囲の相手も遅らせる', 6, 298, [e('skillPower', 0.125)], ['ougi:kazamaru_senpu'], 6],
      ['kamaitachi', '鎌鼬', '中立・ガーディアンへの与ダメ1.3倍', 5, 442, [e('neutral', 0.08), e('atk', 0.03)], ['predator'], 5],
      ['oroshi', '颪', '', 10, 784, [e('cd', -1)], [], 5],
    ],
  ],
  [
    '覚醒',
    [
      ['hayazame', '早覚め', '妖力の弐・参に必要な撃破ポイント-1', 5, 100, [], ['surge:fast:-1'], 6],
      ['oikaze', '追い風の目覚め', '妖力の弐「追い風」の効果1.6倍（疾風の遅らせる量+50%→+80%）', 4, 208, [e('agi', 0.025)], ['surge:boost:kazamaru:2=1.6'], 5],
      ['tengukaze', '天狗風', '妖力の参「一撃離脱」の効果1.5倍（攻撃の後に3マスまで動ける）。参に上がった瞬間、疾風がすぐ使える', 7, 406, [], ['surge:boost:kazamaru:3=1.5', 'surge:on3:skill'], 4],
      ['daitengu', '大天狗', '', 8, 748, [e('atk', 0.05), e('agi', 0.035)], [], 4],
    ],
  ],
]);

const SUZUNE = tree('suzune', [
  [
    '普請',
    [
      ['kokoroe', '大工の心得', '', 3, 1, [e('buildHp', 0.1), e('buildPower', 0.05), e('hp', 0.025)], [], 9],
      ['zumen', '鬼の図面', '', 6, 64, [e('buildPower', 0.04), e('maxCount', 1)], [], 8],
      ['daiku', '鬼の大工', '', 9, 244, [e('buildRange', 1), e('maxCount', 1)], ['ougi:suzune_daiku'], 6],
      ['hayadate', '早建て', '', 8, 496, [e('perTurn', 1), e('atk', 0.03)], [], 5],
    ],
  ],
  [
    '攻城',
    [
      ['kanabojutsu', '金棒術', '', 3, 19, [e('core', 0.065)], [], 8],
      ['kanabo', '鬼の金棒', '', 6, 154, [e('core', 0.06), e('atk', 0.02)], ['ougi:suzune_kanabo'], 7],
      ['shirokuzushi', '城崩し', '', 9, 370, [e('core', 0.1)], [], 6],
      ['ogui', '大物喰い', '', 9, 640, [e('boss', 0.07), e('atk', 0.03)], [], 4],
    ],
  ],
  [
    '守り',
    [
      ['onihada', '鬼の肌', '', 3, 37, [e('hp', 0.05), e('buildHp', 0.05)], [], 8],
      ['fudo', '不動', '自分の建物の隣にいると被ダメ-15%', 6, 280, [e('taken', -0.03)], ['fushin_guard'], 6],
      ['dandori', '鬼の段取り', '手番の最初に2マス以内の自分の建物を最大HPの15%直す', 6, 424, [e('matStart', 2), e('matIncome', 1)], ['fushin_repair'], 5],
      ['toryo', '鬼の棟梁', '自分の建物は一度だけ、壊れる攻撃をHP1で耐える。隣の自分の建物は攻撃の建物の威力+30%・被ダメ-30%。自分の本陣は2マス以内の自分の建物1つごとに被ダメ-30%（最大-60%）', 8, 586, [e('buildHp', 0.07), e('buildPower', 0.07)], ['fushin_endure', 'fushin_lead'], 5],
    ],
  ],
  [
    '覚醒',
    [
      ['hayazame', '早覚め', '妖力の弐・参に必要な撃破ポイント-1', 5, 100, [], ['surge:fast:-1'], 6],
      ['ofushin', '鬼の大普請', '妖力の参「鬼普請」の効果1.6倍（建物HP・攻撃の建物の威力+50%→+80%）', 3, 190, [e('buildHp', 0.07)], ['surge:boost:suzune:3=1.6'], 5],
      ['tegiwa', '鬼の手際', '妖力の弐「鬼の手際」の効果1.5倍（建てられる数・建築の回数・移動 +1→+2）。弐に上がった瞬間、陣材+5', 7, 406, [], ['surge:boost:suzune:2=1.5', 'surge:on2:mat'], 5],
      ['kishin', '鬼神', '', 8, 748, [e('hp', 0.05), e('atk', 0.05)], [], 4],
    ],
  ],
]);

const YUZUHA = tree('yuzuha', [
  [
    '迎撃',
    [
      ['batto', '抜刀', '', 3, 1, [e('atk', 0.04)], [], 9],
      ['iai', '居合', '待機の次の攻撃が1.35倍', 5, 118, [e('atk', 0.025)], ['riposte'], 6],
      ['kaeshigatana', '返し刀', '隣から攻撃されて生き残ると、通常攻撃の100%で反撃', 8, 262, [e('def', 0.08), e('hp', 0.03)], ['kaeshi'], 6],
      ['mamori', '妖狐の守り', '手番の最初にHP5%回復', 7, 622, [e('def', 0.1)], ['regen'], 5],
    ],
  ],
  [
    '狐火',
    [
      ['kitsunebi', '狐火', '', 3, 28, [e('skillPower', 0.08)], [], 8],
      ['kekkai', '狐火結界', '術で与えたダメージの25%が、相手の次の手番の最初に燃える', 5, 316, [e('skillPower', 0.08)], ['ougi:yuzuha_kekkai'], 6],
      ['onibi', '鬼火返し', '中立・ガーディアンへの与ダメ1.3倍', 5, 460, [e('neutral', 0.08)], ['predator'], 5],
      ['yomeiri', '狐の嫁入り', '', 9, 766, [e('cd', -1)], [], 5],
    ],
  ],
  [
    '決闘',
    [
      ['seiki', '生気', '', 3, 46, [e('hp', 0.05)], [], 8],
      ['kyubi', '九尾', '', 6, 208, [e('lifesteal', 0.05), e('hp', 0.04)], ['ougi:yuzuha_kyubi'], 6],
      ['ikkiuchi', '一騎討ち', '相手ヒーローへの与ダメ1.2倍', 8, 352, [e('atk', 0.03)], ['duelist'], 6],
      ['hissatsu', '必殺剣', 'HP35%以下の相手へ与ダメ1.25倍', 8, 550, [e('atk', 0.065)], ['execution'], 5],
    ],
  ],
  [
    '覚醒',
    [
      ['hayazame', '早覚め', '妖力の弐・参に必要な撃破ポイント-1', 5, 100, [], ['surge:fast:-1'], 6],
      ['kawaki', '渇きの目覚め', '妖力の弐「妖狐の渇き」の効果1.6倍（吸精+10%→+16%）', 4, 190, [e('atk', 0.025)], ['surge:boost:yuzuha:2=1.6'], 5],
      ['issen', '一閃の目覚め', '妖力の参「狐火一閃」の効果1.5倍（威力+40%→+60%）。参に上がった瞬間、狐火斬りがすぐ使える', 7, 406, [], ['surge:boost:yuzuha:3=1.5', 'surge:on3:skill'], 4],
      ['kyubinokitsune', '九尾の狐', '', 8, 730, [e('atk', 0.05), e('agi', 0.035)], [], 4],
    ],
  ],
]);

// ---------------- 妖力の奥義（GAME_DESIGN 2.5） ----------------
// 専用ツリーの幹の先に突き出る1つ。主Lv442で解放（第5章クリアのころ）。想定育成（推奨戦力）には入れない（standardProfile が外す）
// 妖力の奥義：術ごとに1つ。その術の系統（専用ツリーの列）の先に置く。持っていく術の奥義を覚えていれば、妖力が参のとき術の枠が奥義になる。
// 基本の術の奥義は前からの id（<キャラ>_ougi）を引き継ぐ（覚えていた人はそのまま）。ほかの術の奥義はその術を覚えるスキルが前提
const OUGI: SkillNode[] = JUTSU.map((j) => {
  const strike = j.kind === 'strike';
  const line = ({ akari: AKARI, kazamaru: KAZAMARU, suzune: SUZUNE, yuzuha: YUZUHA } as Record<CharId, SkillNode[]>)[j.charId].find((s) => s.col === j.col)?.line ?? '奥義';
  const learn = ({ akari: AKARI, kazamaru: KAZAMARU, suzune: SUZUNE, yuzuha: YUZUHA } as Record<CharId, SkillNode[]>)[j.charId].find((s) => s.col === j.col && s.tier === 1);
  return {
    id: strike ? `${j.charId}_ougi` : `${j.charId}_ougi_${j.id}`,
    charId: j.charId,
    group: 'char' as const,
    col: j.col,
    tier: 4,
    line,
    name: `奥義・${j.ougiName}`,
    desc: `術「${j.name}」の奥義。妖力が参のとき、${j.name}の代わりに放てる（${j.ougiDesc}）。放つと妖力は壱に戻る`,
    cost: 3,
    unlockLv: 442,
    requires: strike || !learn ? [] : [learn.id],
    effects: [],
    flags: [`jougi:${j.id}`],
    prio: strike ? 8 : 4,
    surge: true,
  };
});

// 術：専用ツリーの系統の2段目でその系統の術を覚え（基本の術は最初から）、その系統の2〜4段目で術の威力が上がる（jpow:<術>=割合）
const JUTSU_POW = [0, 0.1, 0.15, 0.15];
for (const node of [...AKARI, ...KAZAMARU, ...SUZUNE, ...YUZUHA]) {
  const j = JUTSU.find((x) => x.charId === node.charId && x.col === node.col && x.kind !== 'strike');
  // 系統（skill-lines）のキャラは、術を系統の Lv で覚え、威力も系統の Lv で上がる（技には付けない）
  if (!j || node.tier < 1 || skillLines(node.charId)) continue;
  const pow = JUTSU_POW[node.tier] ?? 0;
  node.flags = [...node.flags, ...(node.tier === 1 ? [`jutsu:${j.id}`] : []), `jpow:${j.id}=${pow}`];
  const text = `${node.tier === 1 ? `術「${j.name}」を覚える・` : ''}${j.name}の威力+${Math.round(pow * 100)}%`;
  node.desc = node.desc ? `${text}。${node.desc}` : text;
}

// 段（skill-ladder）の能力のマス：行ごとに HP・攻撃などから1つ
const STAT_NAME: Partial<Record<ModKey, [string, SkillGroup, number]>> = {
  hp: ['鍛錬', 'body', 6], atk: ['剛力', 'body', 6], def: ['鉄壁', 'body', 5], agi: ['身軽', 'body', 4], core: ['城攻め', 'tactics', 5],
  boss: ['大物狩り', 'tactics', 4], taken: ['受け流し', 'tactics', 5], buildHp: ['頑丈', 'build', 5], buildPower: ['工夫', 'build', 6],
};
const LADDER_NODES: SkillNode[] = LADDER_STATS.map((x) => {
  const [name, group, prio] = STAT_NAME[x.key] ?? [x.key, 'body', 4];
  const fx = [e(x.key, x.value)];
  return { id: x.id, charId: x.charId, group, col: 0, tier: 0, line: name, name, desc: describe('', fx), cost: 0, unlockLv: x.lv, requires: [], effects: fx, flags: [], prio };
});

// 系統（skill-lines）の博徒：賽の目で効く技（4人とも同じ。当たれば大きく、外れると下がる）
const BAKUTO_SPEC: [string, string, Effect[], string][] = [
  ['chouhan', '丁半博打', [e('diceEven', 0.3), e('diceOdd', -0.15)], '丁（賽の合計が偶数）の手番の与ダメ+30%。半（奇数）の手番は被ダメ+15%'],
  ['oome', '大目狙い', [e('diceHigh', 0.5), e('diceLowAtk', -0.2)], '賽の合計が10以上の手番の与ダメ+50%。9以下の手番は与ダメ-20%'],
  ['yakusha', '役者', [e('diceRole', 1.2), e('diceRoleMat', 3), e('diceNoRole', -0.1)], '役が出た手番の与ダメ+120%・陣材+3。役が出なかった手番は与ダメ-10%'],
  ['kiwami_bakuto', 'いかさま賽', [e('diceMin', 1), e('dicePair', 0.4)], ''],
];
const BAKUTO: SkillNode[] = CHAR_IDS.flatMap((c) =>
  BAKUTO_SPEC.map(([id, name, fx, desc], i) => ({ id: `${c}_${id}`, charId: c, group: 'char' as const, col: 4, tier: i, line: '博徒', name, desc: desc || describe('', fx), cost: 0, unlockLv: 1, requires: [], effects: fx, flags: ['bakuto'], prio: 4 })),
);
// 系統（skill-lines）の極み：系統の Lv10 で開く（覚醒の極みは今までの最後の技：鬼神・星降り・大天狗・九尾の狐）
const KIWAMI: SkillNode[] = (
  [
    ['suzune', 'fushin', 0, '名匠', [e('buildHp', 0.12), e('buildPower', 0.1)], '普請'],
    ['suzune', 'kojo', 1, '破城槌', [e('core', 0.06), e('atk', 0.04)], '攻城'],
    ['suzune', 'mamori', 2, '金城鉄壁', [e('hp', 0.07), e('taken', -0.03)], '守り'],
    ['akari', 'hanabi', 0, '千輪', [e('skillPower', 0.08), e('splash', 0.08)], '花火'],
    ['akari', 'jintori', 1, '万灯', [e('territory', 0.015), e('atk', 0.04)], '陣取り'],
    ['akari', 'mamori', 2, '不夜城', [e('hp', 0.07), e('taken', -0.03)], '守り'],
    ['kazamaru', 'sokko', 0, '疾風迅雷', [e('atk', 0.05), e('agi', 0.015)], '速攻'],
    ['kazamaru', 'ridatsu', 1, '隠れ蓑', [e('taken', -0.04), e('agi', 0.02)], '離脱'],
    ['kazamaru', 'kari', 2, '大鎌鼬', [e('skillPower', 0.07), e('neutral', 0.07)], '狩り'],
    ['yuzuha', 'geigeki', 0, '明鏡止水', [e('def', 0.08), e('taken', -0.03)], '迎撃'],
    ['yuzuha', 'kitsunebi', 1, '狐火万里', [e('skillPower', 0.08), e('neutral', 0.05)], '狐火'],
    ['yuzuha', 'kettou', 2, '天下無双', [e('atk', 0.05), e('hp', 0.03)], '決闘'],
  ] as [CharId, string, number, string, Effect[], string][]
).map(([c, k, col, name, fx, line]) => ({ id: `${c}_kiwami_${k}`, charId: c, group: 'char' as const, col, tier: 5, line, name, desc: describe('', fx), cost: 0, unlockLv: 1, requires: [], effects: fx, flags: ['kiwami'], prio: 4 }));

// 系統（skill-lines）の技の説明：技は開いたら全部効く（累積）ので、効果を先に、条件付きの効果を後に
const LINE_DESC: Record<string, string> = {
  suzune_zumen: '建てられる数+1・建物の効果+4%',
  suzune_daiku: '建てられる数+1・建築の射程+1',
  suzune_hayadate: '1手番に2回建てられる・攻撃+3%',
  suzune_kanabo: '本陣への与ダメ+6%・攻撃+2%',
  suzune_shirokuzushi: '本陣への与ダメ+10%',
  suzune_ogui: 'ボスへの与ダメ+7%・攻撃+3%',
  suzune_fudo: '被ダメ-3%。自分の建物の隣にいると、さらに被ダメ-15%',
  suzune_dandori: '初期の陣材+2・毎手番の陣材+1。手番の最初に、2マス以内の自分の建物を最大HPの15%直す',
  suzune_toryo: '建物HP+7%・建物の効果+7%。自分の建物は一度だけ、壊れる攻撃をHP1で耐える。隣の自分の建物は攻撃の威力+30%・被ダメ-30%',
  suzune_hayazame: '妖力の弐・参に上がるのに必要な撃破ポイント-1',
  suzune_tegiwa: '妖力の弐「鬼の手際」が1.5倍（建てられる数・建築の回数・移動 +1→+2）。弐に上がった瞬間、陣材+5',
  suzune_ofushin: '建物HP+7%。妖力の参「鬼普請」が1.6倍（建物HP・攻撃の建物の威力 +50%→+80%）',
};
for (const n of SUZUNE) if (LINE_DESC[n.id]) n.desc = LINE_DESC[n.id];
// アカリ・カザマル・ユズハの技は系統（累積）に合わせて、％の効果を7割に（1%単位。スズネは個別に調整済み）。説明の数値も合わせる
for (const n of [...AKARI, ...KAZAMARU, ...YUZUHA]) {
  if (n.tier < 1) continue;
  const old = new Set(n.effects.map(effectText));
  const note = n.desc.split('・').filter((t) => !old.has(t)).join('・');
  n.effects = n.effects.map((x) => (DISCRETE_KEYS.has(x.key) || x.key === 'cd' || x.key === 'move' ? x : { ...x, value: Math.sign(x.value) * Math.max(0.01, Math.round(Math.abs(x.value) * 0.7 * 100) / 100) }));
  n.desc = describe(note, n.effects);
}

// 系統の Lv8・Lv10（極み）の技：能力の上乗せではなく、戦い方が変わる一捻り（戦闘のフラグ。comp:* はお供の能力と同じ仕組みで、値は足し合わせ）
// [名前, 効果（能力。小さめ）, 戦闘のフラグ, 説明]
const TWIST: Record<string, [string, Effect[], string[], string]> = {
  // スズネ
  suzune_ogui: ['大物喰い', [], ['comp:feast=0.15'], '相手（ヒーロー・妖）を倒すと、最大HPの15%を回復'],
  suzune_kiwami_fushin: ['名匠', [e('buildPower', 0.05)], ['eq_volley'], '自分が攻撃すると、その相手を射程に入れている自分の攻撃の建物も一緒に撃つ（威力60%）・建物の効果+5%'],
  suzune_kiwami_kojo: ['破城槌', [], ['eq_knock', 'comp:trap=0.3'], '通常攻撃で相手を1マス押し出す（押せないときは追加のダメージ）。相手の建物への与ダメ+30%・落とし穴にかからない'],
  suzune_kiwami_mamori: ['金城鉄壁', [], ['comp:kasa=2'], '最大HPの1割以上の攻撃を、1戦に2回まで防ぐ（ダメージ0）'],
  suzune_kishin: ['鬼神', [], ['surge:on3:heal'], '妖力が参に上がった瞬間、HPが全回復'],
  // アカリ
  akari_senka: ['千灯万華', [], ['comp:burn=0.1'], '攻撃・術を当てた相手に、与ダメの10%の火傷（次の手番の最初に燃える）'],
  akari_kiwami_hanabi: ['千輪', [], ['comp:cd=1'], '術を使った後の待ちが1手番短い（1戦に1回）'],
  akari_kiwami_jintori: ['万灯', [], ['comp:relay=0.2', 'comp:dash=2'], '龍脈を取ると最大HPの20%を回復。最初の2手番は移動+1'],
  akari_kiwami_mamori: ['不夜城', [], ['comp:rise=0.2'], '1戦に一度だけ、倒れる攻撃を受けても最大HPの20%で踏みとどまる'],
  akari_hoshifuri: ['星降り', [], ['comp:kuda=2'], '中立を倒すと妖力の撃破ポイント+1（1戦に2回まで）'],
  // カザマル
  kazamaru_kakure: ['天狗隠れ', [], ['comp:kasa=1'], '最大HPの1割以上の攻撃を、1戦に1回だけ防ぐ（ダメージ0）'],
  kazamaru_kiwami_sokko: ['疾風迅雷', [], ['eq_double'], '通常攻撃の後に、もう一度50%の追撃'],
  kazamaru_kiwami_ridatsu: ['羽団扇', [], ['eq_knock', 'comp:dash=2'], '通常攻撃で相手を1マス押し出す（押せないときは追加のダメージ）。最初の2手番は移動+1'],
  kazamaru_kiwami_kari: ['大鎌鼬', [], ['comp:kuda=3', 'eq_cleave'], '通常攻撃で的の周りにも50%。中立を倒すと妖力の撃破ポイント+1（1戦に3回まで）'],
  kazamaru_daitengu: ['大天狗', [], ['comp:slow=2', 'comp:surge=1'], '自分から2マス以内で手番を始めた相手ヒーローの移動-1。最初の手番に妖力の撃破ポイント+1'],
  // ユズハ
  yuzuha_kiwami_geigeki: ['明鏡止水', [], ['comp:mirror=2', 'comp:kasa=1'], '隣から攻撃されて生き残ると、通常攻撃の200%で斬り返す（返し刀と重ならず大きい方）。大きな攻撃を1回防ぐ'],
  yuzuha_kiwami_kitsunebi: ['狐火万里', [], ['comp:burn=0.3', 'comp:cd=1'], '攻撃・術を当てた相手に、与ダメの30%の火傷。術の待ちを1戦に1回、1手番短く'],
  yuzuha_kiwami_kettou: ['天下無双', [], ['eq_bind', 'eq_double'], '通常攻撃の後に、もう一度50%の追撃。通常攻撃を当てた相手を2手番の足止め（移動-1）'],
  yuzuha_kyubinokitsune: ['九尾の狐', [], ['comp:feast=0.2', 'comp:rise=0.2'], '相手を倒すと最大HPの20%を回復。1戦に一度だけ、倒れる攻撃を最大HPの20%で踏みとどまる'],
};
for (const n of [...SUZUNE, ...AKARI, ...KAZAMARU, ...YUZUHA, ...KIWAMI]) {
  const t = TWIST[n.id];
  if (!t) continue;
  [n.name, n.effects, n.flags, n.desc] = [t[0], t[1], [...n.flags.filter((f) => f.startsWith('jutsu:') || f.startsWith('jpow:') || f === 'kiwami'), ...t[2], 'twist'], t[3]];
}

export const SKILLS: readonly SkillNode[] = [...COMMON, ...AKARI, ...KAZAMARU, ...SUZUNE, ...YUZUHA, ...OUGI, ...LADDER_NODES, ...BAKUTO, ...KIWAMI];
export const SKILL_BY_ID: Record<string, SkillNode> = Object.fromEntries(SKILLS.map((s) => [s.id, s]));
export const COMMON_SKILLS: readonly SkillNode[] = COMMON;
/** 基本の術の妖力の奥義のスキル（キャラごと） */
export const ougiSkillId = (c: CharId) => `${c}_ougi`;
/** 妖力の奥義のスキル（術ごと。全キャラ分） */
export const OUGI_SKILL_IDS: readonly string[] = OUGI.map((s) => s.id);
/** そのキャラが装着できるスキル（専用＋共通） */
export const skillsFor = (charId: CharId): SkillNode[] => SKILLS.filter((s) => s.charId === null || s.charId === charId);
/** 1人分の全スキルの総コスト */
export const totalSkillCost = (charId: CharId) => skillsFor(charId).reduce((a, s) => a + s.cost, 0);

// ---------------- 解放 ----------------

export const isSkillUnlocked = (s: SkillNode, level: number) => level >= s.unlockLv;
/** その主Lvでちょうど解放されるスキル（全キャラ分。共通は1つ） */
export const skillsUnlockedAt = (level: number): SkillNode[] => SKILLS.filter((s) => s.unlockLv === level);
/** prevLv → newLv のレベルアップで新しく解放されたスキル（prevLv < 解放Lv ≤ newLv） */
// 系統（skill-lines）のキャラの技は主Lvでは解放されない（系統の Lv で開く）ので含めない
export const newlyUnlocked = (prevLv: number, newLv: number, charId?: CharId): SkillNode[] =>
  SKILLS.filter((s) => s.unlockLv > prevLv && s.unlockLv <= newLv && (s.charId ? !skillLines(s.charId) : !CHAR_IDS.every((c) => skillLines(c))) && (charId === undefined || s.charId === null || s.charId === charId));
/** 次に解放されるスキルの主Lv（無ければ null） */
export function nextUnlockLevel(level: number): number | null {
  let best: number | null = null;
  for (const s of SKILLS) if (s.unlockLv > level && (best === null || s.unlockLv < best)) best = s.unlockLv;
  return best;
}

// ---------------- 解放力 ----------------

export interface CapacityInfo {
  total: number;
  base: number;
  level: number;
  shikigami: number;
  dojo: number;
  gaiden: number;
  /** 上乗せの合計（bonusMax で頭打ち） */
  bonus: number;
}

export function skillCapacity(p: Profile): CapacityInfo {
  const c = CONFIG.skills.capacity;
  const shikiN = Object.values(p.shikigami ?? {}).filter((s) => s.star > 0).length;
  const shikigami = Math.min(c.shikiMax, Math.floor(shikiN / c.shikiPer));
  const dojo = Math.min(c.dojoMax, Math.floor((p.village?.levels?.dojo ?? 0) / c.dojoPer));
  const gaiden = Math.min(c.gaidenMax, (p.gaidenCleared ?? []).length * c.gaidenEach);
  const bonus = Math.min(c.bonusMax, shikigami + dojo + gaiden);
  // 旧Lv1つ（主Lv9つ）ごとに perLevel（Lv1で1・Lv10で2 …）
  const level = Math.floor(levelTier(p.level) * c.perLevel);
  return { total: c.base + level + bonus, base: c.base, level, shikigami, dojo, gaiden, bonus };
}

// ---------------- 構成 ----------------

export interface CharSkills {
  /** 装着中（auto のときは autoBuild の結果を入れておく。読むときは equippedSkills を使う） */
  set: string[];
  /** おすすめ構成に任せる（主Lv・解放力が上がるたびに組み直す） */
  auto: boolean;
  /** 保存枠（無料1枠、権利 build_slots で10枠） */
  presets: (string[] | null)[];
  /** おすすめビルドの型（styles.ts のキー）。auto のとき、おすすめの代わりにこの型で組む（主Lvが上がっても型のまま） */
  style?: string;
  /** 持っていく術（data/jutsu.ts の id）。覚えていなければ基本の術 */
  jutsu?: string;
  /** 想定育成（推奨戦力）用：系統 Lv7 でも術の奥義を付けない */
  noOugi?: boolean;
}

export const newCharSkills = (): CharSkills => ({ set: [], auto: true, presets: [null, null] });
export const newSkillState = (): Record<CharId, CharSkills> => Object.fromEntries(CHAR_IDS.map((c) => [c, newCharSkills()])) as Record<CharId, CharSkills>;


/**
 * 網のキャラの解放Lv：そのマスに届く道の途中で、いちばん遅く解放されるマスの Lv（自分より先に解放されても、道が開くまでは取れないので）。
 * 網の無いキャラ・網に無いスキルはそのスキルの解放Lv
 */
const unlockCache = new Map<CharId, Map<string, number>>();
export function unlockLvFor(charId: CharId, s: SkillNode): number {
  const at = ladderAt(charId, s.id);
  if (at) return skillLadder(charId)![at.row].lv;
  const g = skillGraph(charId);
  if (!g?.pos[s.id]) return s.unlockLv;
  let m = unlockCache.get(charId);
  if (!m) {
    m = new Map([[GRAPH_CORE, 0]]);
    const open = [GRAPH_CORE];
    while (open.length) {
      open.sort((a, b) => m!.get(a)! - m!.get(b)!);
      const x = open.shift()!;
      for (const n of graphNeighbors(charId, x)) {
        const sn = SKILL_BY_ID[n];
        if (!sn) continue;
        const v = Math.max(m.get(x)!, sn.unlockLv);
        if (v < (m.get(n) ?? Infinity)) (m.set(n, v), open.push(n));
      }
    }
    unlockCache.set(charId, m);
  }
  return m.get(s.id) ?? s.unlockLv;
}
const unlockedFor = (charId: CharId, s: SkillNode, level: number) => level >= unlockLvFor(charId, s);

export const costOf = (ids: readonly string[]) => ids.reduce((a, id) => a + (isLineToken(id) ? lineCost(Number(id.split('=')[1])) : (SKILL_BY_ID[id]?.cost ?? 0)), 0);

/**
 * 前提を満たすか。網のあるキャラ（skill-graph）は、取ったマス（または中心）の隣ならよい（奥義は術を覚えるスキルも要る）。
 * 網の無いキャラは今までどおり requires をすべて
 */
export function requiresMet(charId: CharId, s: SkillNode, has: ReadonlySet<string>): boolean {
  const g = skillGraph(charId);
  if (g?.pos[s.id]) {
    if (!graphNeighbors(charId, s.id).some((n) => n === GRAPH_CORE || has.has(n))) return false;
    return !s.flags.some((f) => f.startsWith('jougi:')) || s.requires.every((r) => has.has(r));
  }
  return s.requires.every((r) => has.has(r));
}
/** 構成のうち、前提がつながっているものだけ（網：中心からつながる・それ以外：前提の連鎖） */
function connected(charId: CharId, ids: readonly string[]): Set<string> {
  const g = skillGraph(charId);
  const has = new Set(ids);
  if (!g) return has;
  const reach = graphReach(charId, has);
  const out = new Set<string>();
  for (const id of ids) {
    const s = SKILL_BY_ID[id];
    if (!s) continue;
    if (g.pos[id] ? reach.has(id) && (!s.flags.some((f) => f.startsWith('jougi:')) || s.requires.every((r) => has.has(r))) : s.requires.every((r) => has.has(r))) out.add(id);
  }
  return out;
}
const charOfIds = (ids: readonly string[]): CharId | null => (ids.map((id) => SKILL_BY_ID[id]?.charId).find((c) => c) ?? null) as CharId | null;

// ---------------- 段（skill-ladder）：各行から1つ。選んだマスの真上・斜め上へ ----------------

/** 下の行から順に、ids の中でその行に選ばれていて、下の行とつながっているものを並べる（途切れたらそこまで） */
function ladderPrefix(charId: CharId, level: number, ids: readonly string[]): string[] {
  const L = skillLadder(charId)!;
  const open = ladderOpen(charId, level);
  const out: string[] = [];
  for (let r = 0; r < open; r++) {
    const id = L[r].ids.find((x) => ids.includes(x));
    if (!id) break;
    if (r > 0 && !ladderLinked(L[r - 1].ids.length, ladderAt(charId, out[r - 1])!.i, L[r].ids.length, ladderAt(charId, id)!.i)) break;
    out.push(id);
  }
  return out;
}
/** 決まっている下の行（fixed）から上へ、開いている行を score の合計がいちばん大きい道で埋める */
function ladderBest(p: Profile, charId: CharId, score: (id: string) => number, fixed: readonly string[] = []): string[] {
  const L = skillLadder(charId)!;
  const open = ladderOpen(charId, p.level);
  const out = fixed.slice(0, open);
  const start = out.length;
  if (start >= open) return out;
  const prevI = start > 0 ? ladderAt(charId, out[start - 1])!.i : -1;
  let best = L[start].ids.map((id, j) => (start > 0 && !ladderLinked(L[start - 1].ids.length, prevI, L[start].ids.length, j) ? -Infinity : score(id)));
  const back: number[][] = [];
  for (let r = start + 1; r < open; r++) {
    const a = L[r - 1].ids.length;
    const b = L[r].ids.length;
    const from: number[] = [];
    const next = L[r].ids.map((id, j) => {
      let m = -Infinity;
      let k = 0;
      for (let i = 0; i < a; i++) if (ladderLinked(a, i, b, j) && best[i] > m) (m = best[i]), (k = i);
      from.push(k);
      return m + score(id);
    });
    back.push(from);
    best = next;
  }
  let j = best.indexOf(Math.max(...best));
  const path: number[] = [j];
  for (let r = back.length - 1; r >= 0; r--) path.unshift((j = back[r][j]));
  path.forEach((jj, k) => out.push(L[start + k].ids[jj]));
  return out;
}
/** おすすめの点数（引き換えのある技・持っていかない術の奥義は避ける） */
const ladderScore = (exclude: readonly string[]) => (id: string) => {
  const s = SKILL_BY_ID[id];
  if (!s) return -1e6;
  if (exclude.includes(id)) return -50;
  if (s.flags.some((f) => TRADEOFF_FLAGS.includes(f))) return -100;
  return s.prio;
};
/**
 * 段でそのマスを選んだ構成（画面の「これにする」）。下の行がつながっていなければ、近いマスに組み替えて道をつなぐ。
 * 上の行も、つながらなくなったところは近いマスに替える。開いていない行・段に無いマスは null
 */
export function ladderPick(p: Profile, charId: CharId, current: readonly string[], id: string): string[] | null {
  const L = skillLadder(charId);
  const at = ladderAt(charId, id);
  if (!L || !at || at.row >= ladderOpen(charId, p.level)) return null;
  const out = sanitize(p, charId, current);
  out[at.row] = id;
  const fix = (r: number, nb: number) => {
    const n = L[r].ids.length;
    const m = L[nb].ids.length;
    const ni = ladderAt(charId, out[nb])!.i;
    const ci = ladderAt(charId, out[r])!.i;
    if (ladderLinked(n, ci, m, ni)) return;
    let bj = 0;
    let bd = Infinity;
    for (let j = 0; j < n; j++) {
      if (!ladderLinked(n, j, m, ni)) continue;
      const d = Math.abs(ladderX(n, j) - ladderX(n, ci));
      if (d < bd) (bd = d), (bj = j);
    }
    out[r] = L[r].ids[bj];
  };
  for (let r = at.row - 1; r >= 0; r--) fix(r, r + 1);
  for (let r = at.row + 1; r < out.length; r++) fix(r, r - 1);
  return out;
}
// ---------------- 系統（skill-lines）：系統の Lv をポイントで上げると、能力が上がり、Lv2・5・8 で技、Lv10 で極みが開く（累積。選ばない）。術は Lv3、奥義は Lv7 ----------------

/** 系統のポイント（主Lvと、解放力の上乗せで増える） */
export const linePointsOf = (p: Profile) => linePoints(p.level, skillCapacity(p).bonus);
/** 主Lv が before→after に上がって増えた系統のポイント（結果画面・TOP の知らせ） */
export const linePointsGain = (p: Profile, before: number, after: number) => linePoints(after, skillCapacity(p).bonus) - linePoints(before, skillCapacity(p).bonus);
/** 系統のキャラごとの、振っていない残りのポイント（手動のときだけ残る。おまかせは全部振る） */
export function unspentLinePoints(p: Profile): { charId: CharId; left: number }[] {
  return CHAR_IDS.filter((c) => skillLines(c)).map((c) => ({ charId: c, left: linePointsOf(p) - lineLevels(c, equippedSkills(p, c)).reduce((a, n) => a + lineCost(n), 0) })).filter((x) => x.left > 0);
}
/** 系統 Lv で覚えている術（基本の術＋Lv が LINE_JUTSU_LV 以上の系統の術） */
function lineLearned(charId: CharId, lv: readonly number[]): string[] {
  const out = [defaultJutsu(charId).id];
  (skillLines(charId) ?? []).forEach((l, i) => {
    const j = JUTSU.find((x) => x.charId === charId && x.col === l.col && x.kind !== 'strike');
    if (j && lv[i] >= LINE_JUTSU_LV) out.push(j.id);
  });
  return out;
}
/** 系統の構成の検証（系統 Lv の印だけ・系統ごとに1つ・ポイントの範囲） */
function lineValidate(p: Profile, charId: CharId, ids: readonly string[]): OpResult<{ cost: number; capacity: number }> {
  const L = skillLines(charId)!;
  const seen = new Set<number>();
  for (const id of ids) {
    if (!isLineToken(id)) return { ok: false, error: SKILL_BY_ID[id] ? 'other_char' : 'unknown_skill' };
    const [i, n] = id.slice(4).split('=').map(Number);
    if (i >= L.length || n < 1 || n > LINE_MAX || seen.has(i)) return { ok: false, error: 'skills' };
    seen.add(i);
  }
  const cost = costOf(ids);
  const capacity = linePointsOf(p);
  if (cost > capacity) return { ok: false, error: 'capacity' };
  return { ok: true, cost, capacity };
}
/** 系統 Lv から構成の ID の並び */
const lineIds = (lv: readonly number[]) => lv.flatMap((n, i) => (n > 0 ? [lineToken(i, n)] : []));
/** 保存値を有効な形に（ポイントを超えたら Lv の高い系統から下げる） */
function lineSanitize(p: Profile, charId: CharId, ids: readonly string[]): string[] {
  // 前の仕組みの構成（系統の Lv の印が無い）は、おすすめで組み直す
  if (ids.length && !ids.some(isLineToken)) return lineAuto(p, charId, linePointsOf(p));
  const lv = lineLevels(charId, ids);
  const cap = linePointsOf(p);
  while (lv.reduce((a, n) => a + lineCost(n), 0) > cap) lv[lv.indexOf(Math.max(...lv))]--;
  return lineIds(lv);
}
/**
 * おすすめ：ポイントを系統へ。重み weights の大きい系統ほど高い Lv に（持っていく術の系統は少し厚く：術の Lv3・奥義の Lv7 へ）。
 * 技の開く Lv（2・5・8・10）に届くところで止まるように、1つずつ「次に技が開くまでのポイント÷重み」の小さい系統へ
 */
function lineAuto(p: Profile, charId: CharId, capacity: number, o: { weights?: number[]; jutsu?: string } = {}): string[] {
  const L = skillLines(charId)!;
  const carried = JUTSU_BY_ID[o.jutsu ?? p.skills?.[charId]?.jutsu ?? ''] ?? defaultJutsu(charId);
  const w = (o.weights ?? L.map((l) => (l.key === 'bakuto' ? 0.7 : l.key === 'fushin' ? 1.2 : 1))).map((x, i) => x + (L[i].col === carried.col ? 0.5 : 0));
  const lv = L.map(() => 0);
  let left = capacity;
  const nextStop = (n: number) => [...WAZA_LVS, LINE_JUTSU_LV, LINE_OUGI_LV].filter((x) => x > n).sort((a, b) => a - b)[0] ?? LINE_MAX;
  while (left > 0) {
    let best = -1;
    let bestV = Infinity;
    for (let i = 0; i < L.length; i++) {
      if (lv[i] >= LINE_MAX) continue;
      const need = Math.min(left, nextStop(lv[i]) - lv[i]);
      const v = (lv[i] + need) / w[i];
      if (v < bestV) (bestV = v), (best = i);
    }
    if (best < 0) break;
    lv[best]++;
    left--;
  }
  return lineIds(lv);
}
/** 系統の Lv を1つ上げ下げした構成（画面の＋・−）。上げられなければ null */
export function lineStep(p: Profile, charId: CharId, current: readonly string[], line: number, d: 1 | -1): string[] | null {
  const L = skillLines(charId);
  if (!L || line < 0 || line >= L.length) return null;
  const lv = lineLevels(charId, sanitize(p, charId, current));
  const n = lv[line] + d;
  if (n < 0 || n > LINE_MAX) return null;
  lv[line] = n;
  if (lv.reduce((a, x) => a + lineCost(x), 0) > linePointsOf(p)) return null;
  return lineIds(lv);
}
/** 系統 Lv で開いている技・極み（画面・戦闘の効果に使う） */
export function lineWaza(charId: CharId, ids: readonly string[]): string[] {
  const lv = lineLevels(charId, ids);
  return (skillLines(charId) ?? []).flatMap((l, i) => lineOpenWaza(l, lv[i]));
}

/** 段のマスの選び替えで、変わる行（id を選んだとき、今の構成から入れ替わるもの） */
export function ladderChanges(p: Profile, charId: CharId, current: readonly string[], id: string): { from: string; to: string }[] {
  const cur = sanitize(p, charId, current);
  const next = ladderPick(p, charId, current, id) ?? cur;
  return next.map((to, r) => ({ from: cur[r], to })).filter((x) => x.from !== x.to);
}

/** 構成の検証（解放済み・前提・コスト合計 ≤ 解放力・そのキャラのもの・重複なし） */
export function validateBuild(p: Profile, charId: CharId, ids: readonly string[]): OpResult<{ cost: number; capacity: number }> {
  if (!Array.isArray(ids) || ids.length > 200) return { ok: false, error: 'skills' };
  if (skillLines(charId)) return lineValidate(p, charId, ids);
  const seen = new Set<string>();
  for (const id of ids) {
    const s = SKILL_BY_ID[id];
    if (!s) return { ok: false, error: 'unknown_skill' };
    if (s.charId !== null && s.charId !== charId) return { ok: false, error: 'other_char' };
    if (seen.has(id)) return { ok: false, error: 'duplicate' };
    seen.add(id);
    if (!unlockedFor(charId, s, p.level)) return { ok: false, error: 'locked' };
  }
  if (skillLadder(charId)) {
    // 段：段のマスだけ、1行1つ、下の行から途切れずつながる（解放力は使わない）
    if (ids.some((id) => !ladderAt(charId, id))) return { ok: false, error: 'requires' };
    if (new Set(ids.map((id) => ladderAt(charId, id)!.row)).size !== ids.length) return { ok: false, error: 'duplicate' };
    if (ladderPrefix(charId, p.level, ids).length !== ids.length) return { ok: false, error: 'requires' };
    return { ok: true, cost: 0, capacity: 0 };
  }
  if (skillGraph(charId)) {
    if (connected(charId, ids).size !== ids.length) return { ok: false, error: 'requires' };
  } else for (const id of ids) for (const r of SKILL_BY_ID[id].requires) if (!seen.has(r)) return { ok: false, error: 'requires' };
  const cost = costOf(ids);
  const capacity = skillCapacity(p).total;
  if (cost > capacity) return { ok: false, error: 'capacity' };
  return { ok: true, cost, capacity };
}

/** そのスキルを今の構成に足せるか（画面の「装着」ボタン用。理由つき） */
export function canEquipSkill(p: Profile, charId: CharId, current: readonly string[], id: string): OpResult {
  const s = SKILL_BY_ID[id];
  if (!s) return { ok: false, error: 'unknown_skill' };
  if (s.charId !== null && s.charId !== charId) return { ok: false, error: 'other_char' };
  if (current.includes(id)) return { ok: false, error: 'duplicate' };
  if (!unlockedFor(charId, s, p.level)) return { ok: false, error: 'locked' };
  if (skillLadder(charId)) return ladderAt(charId, id) ? { ok: true } : { ok: false, error: 'requires' };
  // 系統：技は選ばない（系統 Lv で開く）
  if (skillLines(charId)) return { ok: false, error: 'requires' };
  if (!requiresMet(charId, s, new Set(current))) return { ok: false, error: 'requires' };
  if (costOf(current) + s.cost > skillCapacity(p).total) return { ok: false, error: 'capacity' };
  return { ok: true };
}

/** 外すと一緒に外れるもの（そのスキルを前提にしているもの、を辿る） */
export function dependentsOf(id: string, current: readonly string[], charId: CharId | null = charOfIds([id, ...current])): string[] {
  if (charId && (skillLadder(charId) || skillLines(charId))) return [id];
  // 網：外したマスから先、中心につながらなくなるもの
  if (charId && skillGraph(charId)) {
    const keep = connected(charId, current.filter((x) => x !== id));
    return current.filter((x) => x === id || !keep.has(x));
  }
  const out = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const x of current) if (!out.has(x) && SKILL_BY_ID[x]?.requires.some((r) => out.has(r))) (out.add(x), (grew = true));
  }
  return current.filter((x) => out.has(x));
}

let prioCache = { ver: -1, map: new Map<CharId, SkillNode[]>() };
/** おすすめの優先度順（キャラの系統に合う共通スキルを少し上げる） */
function priorityOrder(charId: CharId): SkillNode[] {
  if (prioCache.ver !== configVersion()) prioCache = { ver: configVersion(), map: new Map() };
  const hit = prioCache.map.get(charId);
  if (hit) return hit;
  const lean = CONFIG.skills.autoLean[charId] ?? {};
  const list = skillsFor(charId)
    .map((s, i) => ({ s, i, v: s.prio + (s.group !== 'char' ? (lean[s.group] ?? 0) : 0) }))
    .sort((a, b) => b.v - a.v || a.s.unlockLv - b.s.unlockLv || a.i - b.i)
    .map((x) => x.s);
  prioCache.map.set(charId, list);
  return list;
}

/**
 * おすすめ構成：その主Lvで解放済みのスキルから、解放力の範囲で優先度の高い順に入れる（前提がそろうまで繰り返す）。
 * capacity を渡すとその値で組む（想定育成・テスト用）。
 */
/** おすすめでは取らない、引き換えのあるスキル（城崩し：建てられる建物が1つまで）。型で選んだときだけ入る */
const TRADEOFF_FLAGS = ['buildCap1'];


/** 網：取ったマス（と中心）から id まで、解放済みのマスを通るいちばん安い道を acc に足す（届かなければ null）。奥義は術を覚えるスキルへの道も */
function graphPath(p: Profile, charId: CharId, chosen: readonly string[], id: string, acc: string[], avoid: (id: string) => boolean = () => false): string[] | null {
  const have = new Set([...chosen, ...acc]);
  if (have.has(id)) return acc;
  const ok = (x: string) => { const n = SKILL_BY_ID[x]; return !!n && (x === id || !avoid(x)) && (n.charId === null || n.charId === charId) && unlockedFor(charId, n, p.level); };
  const g = skillGraph(charId)!;
  const dist = new Map<string, number>([[GRAPH_CORE, 0]]);
  const prev = new Map<string, string>();
  for (const h of have) if (g.pos[h]) dist.set(h, 0);
  const open = [...dist.keys()];
  while (open.length) {
    open.sort((a, b) => dist.get(a)! - dist.get(b)!);
    const x = open.shift()!;
    if (x === id) break;
    for (const n of graphNeighbors(charId, x)) {
      if (n === GRAPH_CORE || !ok(n)) continue;
      const d = dist.get(x)! + (have.has(n) ? 0 : SKILL_BY_ID[n].cost);
      if (d < (dist.get(n) ?? Infinity)) (dist.set(n, d), prev.set(n, x), open.push(n));
    }
  }
  if (!dist.has(id)) return null;
  const path: string[] = [];
  for (let x: string | undefined = id; x && !have.has(x) && x !== GRAPH_CORE; x = prev.get(x)) path.unshift(x);
  acc.push(...path);
  const s = SKILL_BY_ID[id];
  if (s.flags.some((f) => f.startsWith('jougi:'))) for (const r of s.requires) if (!acc.includes(r) && !chosen.includes(r) && !graphPath(p, charId, chosen, r, acc, avoid)) return null;
  return acc;
}

export function autoBuild(p: Profile, charId: CharId, capacity = skillCapacity(p).total, exclude: readonly string[] = []): string[] {
  if (skillLadder(charId)) return ladderBest(p, charId, ladderScore(exclude));
  if (skillLines(charId)) return lineAuto(p, charId, linePointsOf(p));
  const order = priorityOrder(charId).filter((s) => !exclude.includes(s.id) && !s.flags.some((f) => TRADEOFF_FLAGS.includes(f)));
  if (skillGraph(charId)) {
    // 網：優先度の高い順に、そこまでの道ごと足す（道の費用が解放力に収まるものだけ）
    const chosen: string[] = [];
    let left = capacity;
    // 奥義は道が長いので先に（解放されていれば、道ごと取る）
    const ougiFirst = [...order.filter((s) => s.flags.some((f) => f.startsWith('jougi:'))), ...order.filter((s) => !s.flags.some((f) => f.startsWith('jougi:')))];
    for (const s of ougiFirst) {
      if (chosen.includes(s.id) || !unlockedFor(charId, s, p.level)) continue;
      const add = graphPath(p, charId, chosen, s.id, [], (x) => exclude.includes(x) || SKILL_BY_ID[x].flags.some((f) => TRADEOFF_FLAGS.includes(f)));
      if (!add) continue;
      const cost = costOf(add);
      if (cost > left) continue;
      chosen.push(...add);
      left -= cost;
    }
    return chosen;
  }
  const chosen: string[] = [];
  const has = new Set<string>();
  let left = capacity;
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of order) {
      if (has.has(s.id) || s.cost > left || !isSkillUnlocked(s, p.level)) continue;
      if (!requiresMet(charId, s, has)) continue;
      chosen.push(s.id);
      has.add(s.id);
      left -= s.cost;
      grew = true;
      break; // 優先度の高いものから選び直す（前提が満たされて上位が入れるようになるため）
    }
  }
  return chosen;
}

/** 型の 'line:<共通の系統>' → 段の能力のマスの種類 */
const LINE_KEY: Record<string, ModKey> = { c_hp: 'hp', c_atk: 'atk', c_def: 'def', c_agi: 'agi', c_core: 'core', c_boss: 'boss', c_guard: 'taken', c_bhp: 'buildHp', c_bpow: 'buildPower', c_bp: 'buildPower' };

/** 実際に効いている構成。auto ならおすすめ、手動なら保存値のうち有効なもの（足りなくなったら後ろから外す） */
/** おすすめで取らない奥義（持っていくつもりの術のほかの術の奥義） */
function otherOugi(p: Profile, charId: CharId, jutsu = p.skills?.[charId]?.jutsu): string[] {
  const want = jutsu && JUTSU_BY_ID[jutsu]?.charId === charId ? jutsu : defaultJutsu(charId).id;
  return OUGI.filter((s) => s.charId === charId && !s.flags.includes(`jougi:${want}`)).map((s) => s.id);
}

export function equippedSkills(p: Profile, charId: CharId): string[] {
  const st = p.skills?.[charId];
  if (!st || st.auto) return st?.style ? styleBuild(p, charId, st.style) : autoBuild(p, charId, undefined, otherOugi(p, charId));
  return sanitize(p, charId, st.set);
}

/** 保存値を有効な形にそろえる（未知・他キャラ・未解放・前提なしを外し、解放力を超えた分を後ろから外す） */
export function sanitize(p: Profile, charId: CharId, ids: readonly string[]): string[] {
  if (skillLines(charId)) return lineSanitize(p, charId, ids);
  // 段：つながっている下の行は残し、その先（新しく開いた行など）はおすすめで埋める
  if (skillLadder(charId)) return ladderBest(p, charId, ladderScore(otherOugi(p, charId)), ladderPrefix(charId, p.level, ids));
  const cap = skillCapacity(p).total;
  const out: string[] = [];
  for (const id of ids) {
    const s = SKILL_BY_ID[id];
    if (!s || (s.charId !== null && s.charId !== charId) || out.includes(id) || !unlockedFor(charId, s, p.level)) continue;
    out.push(id);
  }
  // 前提が欠けたものを外す（連鎖。網は中心からつながるものだけ）
  if (skillGraph(charId)) {
    const keep = connected(charId, out);
    for (let i = out.length - 1; i >= 0; i--) if (!keep.has(out[i])) out.splice(i, 1);
  }
  let changed = !skillGraph(charId);
  while (changed) {
    changed = false;
    for (let i = out.length - 1; i >= 0; i--)
      if (!SKILL_BY_ID[out[i]].requires.every((r) => out.includes(r))) (out.splice(i, 1), (changed = true));
  }
  while (costOf(out) > cap && out.length) {
    const last = out[out.length - 1];
    for (const x of dependentsOf(last, out, charId)) out.splice(out.indexOf(x), 1);
  }
  return out;
}

function ensure(p: Profile, charId: CharId): CharSkills {
  if (!p.skills) p.skills = newSkillState();
  if (!p.skills[charId]) p.skills[charId] = newCharSkills();
  return p.skills[charId];
}

/** 構成を決める（手動。auto は切れる） */
export function setSkills(p: Profile, charId: CharId, ids: readonly string[]): OpResult<{ set: string[]; cost: number; capacity: number }> {
  const v = validateBuild(p, charId, ids);
  if (!v.ok) return v;
  const st = ensure(p, charId);
  st.set = [...ids];
  st.auto = false;
  return { ok: true, set: st.set, cost: v.cost, capacity: v.capacity };
}

/** おすすめ構成にする（auto にする：以降は主Lv・解放力が上がるたびに組み直す） */
export function applyAutoBuild(p: Profile, charId: CharId): { set: string[] } {
  const st = ensure(p, charId);
  st.auto = true;
  delete st.style;
  st.set = autoBuild(p, charId, undefined, otherOugi(p, charId));
  return { set: st.set };
}

/** おすすめビルドの型でスキルを組む（auto のまま：主Lv・解放力が上がるたびに型で組み直す） */
export function applyStyleSkills(p: Profile, charId: CharId, key: string): { set: string[] } {
  const st = ensure(p, charId);
  st.auto = true;
  st.style = key;
  st.set = styleBuild(p, charId, key);
  // 型の術を持っていく（まだ覚えていなければ、覚えたときから使う。それまでは基本の術）
  const sj = styleOf(charId, key)?.jutsu;
  if (sj) st.jutsu = sj;
  return { set: st.set };
}

/**
 * 型の優先リストから解放力の範囲で組む（前提を足しながら）。余りはおすすめの順で埋める。
 * 'line:<接頭辞>' は共通スキルの系統（c_hp → c_hp1, c_hp2, …）、それ以外はキャラ専用のキー（<キャラ>_<キー>）
 */
export function styleBuild(p: Profile, charId: CharId, key: string): string[] {
  const style = styleOf(charId, key);
  if (!style) return autoBuild(p, charId, undefined, otherOugi(p, charId));
  // 型の術の奥義も取る（系統の先。主Lvで解放されたら）
  const ougiWish = OUGI.filter((s) => s.charId === charId && s.flags.includes(`jougi:${style.jutsu ?? defaultJutsu(charId).id}`)).map((s) => s.id);
  const wish = [...ougiWish.map((id) => `__${id}`), ...style.skills].flatMap((k) =>
    k.startsWith('__') ? [k.slice(2)] :
    k.startsWith('line:') ? Object.keys(SKILL_BY_ID).filter((id) => id.startsWith(k.slice(5)) && /^\d+$/.test(id.slice(k.length - 5))).sort((a, b) => Number(a.slice(k.length - 5)) - Number(b.slice(k.length - 5))) : [`${charId}_${k}`],
  );
  if (skillLines(charId)) {
    // 系統：型の技のある系統・型の能力の系統を厚く（深い Lv の技ほど重く）
    const L = skillLines(charId)!;
    const keys = style.skills.filter((k) => k.startsWith('line:')).map((k) => LINE_KEY[k.slice(5)]).filter(Boolean);
    const weights = L.map((l) => 1 + l.waza.reduce((a, w, k) => a + (wish.includes(w) ? 0.4 + 0.2 * k : 0), 0) + keys.filter((k) => k in l.per).length * 0.4);
    return lineAuto(p, charId, linePointsOf(p), { weights, jutsu: style.jutsu });
  }
  if (skillLadder(charId)) {
    // 段：型のスキル・術の奥義を大きく、型の系統の能力を少し上げて、いちばん点の高い道
    const want = new Set(wish);
    const keys = new Set(style.skills.filter((k) => k.startsWith('line:')).map((k) => LINE_KEY[k.slice(5)]).filter(Boolean));
    const base = ladderScore(otherOugi(p, charId, style.jutsu));
    return ladderBest(p, charId, (id) => {
      const s = SKILL_BY_ID[id];
      if (want.has(id)) return 50 + (s?.prio ?? 0);
      const v = base(id);
      return v + (s && (keys.has(s.effects[0]?.key) || style.skills.some((k) => k.startsWith('line:') && id.startsWith(k.slice(5)))) ? 4 : 0);
    });
  }
  const cap = skillCapacity(p).total;
  const chosen: string[] = [];
  let used = 0;
  const graph = skillGraph(charId);
  const pathTo = (id: string, acc: string[]) => graphPath(p, charId, chosen, id, acc);
  const chain = (id: string, acc: string[]): string[] | null => {
    if (graph?.pos[id]) return pathTo(id, acc);
    const s = SKILL_BY_ID[id];
    if (!s || (s.charId !== null && s.charId !== charId) || !isSkillUnlocked(s, p.level)) return null;
    for (const r of s.requires) if (!chosen.includes(r) && !acc.includes(r) && !chain(r, acc)) return null;
    if (!chosen.includes(id) && !acc.includes(id)) acc.push(id);
    return acc;
  };
  const tryAdd = (id: string) => {
    if (chosen.includes(id)) return;
    const add = chain(id, []);
    if (!add) return;
    const cost = add.reduce((a, x) => a + SKILL_BY_ID[x].cost, 0);
    if (used + cost > cap) return;
    chosen.push(...add);
    used += cost;
  };
  for (const id of wish) tryAdd(id);
  for (const id of autoBuild(p, charId, 999, otherOugi(p, charId, style.jutsu))) tryAdd(id);
  return chosen;
}

/** auto のキャラの set を今の主Lv・解放力に合わせて書き直す（保存前・表示前に呼ぶ） */
export function refreshAutoSkills(p: Profile) {
  for (const c of CHAR_IDS) {
    const st = ensure(p, c);
    if (st.auto) st.set = st.style ? styleBuild(p, c, st.style) : autoBuild(p, c, undefined, otherOugi(p, c));
  }
}

export const BUILD_SLOT_KEY = 'build_slots';
export const presetSlots = (p: Profile) => (p.entitlements.includes(BUILD_SLOT_KEY) ? CONFIG.skills.presetSlots : CONFIG.skills.freePresetSlots);

/** 今の構成を保存枠に入れる */
export function savePreset(p: Profile, charId: CharId, slot: number): OpResult<{ presets: (string[] | null)[] }> {
  if (!Number.isInteger(slot) || slot < 0 || slot >= CONFIG.skills.presetSlots) return { ok: false, error: 'slot' };
  if (slot >= presetSlots(p)) return { ok: false, error: 'not_owned' };
  const st = ensure(p, charId);
  while (st.presets.length < CONFIG.skills.presetSlots) st.presets.push(null);
  st.presets[slot] = equippedSkills(p, charId);
  return { ok: true, presets: st.presets };
}

/** 保存枠の構成に切り替える（手動になる。今の主Lvで使えないものは外す） */
export function loadPreset(p: Profile, charId: CharId, slot: number): OpResult<{ set: string[] }> {
  if (!Number.isInteger(slot) || slot < 0 || slot >= CONFIG.skills.presetSlots) return { ok: false, error: 'slot' };
  if (slot >= presetSlots(p)) return { ok: false, error: 'not_owned' };
  const st = ensure(p, charId);
  const saved = st.presets[slot];
  if (!saved) return { ok: false, error: 'empty' };
  st.set = sanitize(p, charId, saved);
  st.auto = false;
  return { ok: true, set: st.set };
}

/** スキルの効果をキャラごとに合算 */
export function skillMods(p: Profile, charId: CharId, ids: readonly string[] = equippedSkills(p, charId)): { mods: Mods; flags: string[] } {
  const mods: Mods = {};
  const flags: string[] = [];
  const mult = CONFIG.skills.effectMult;
  const add = (x: Effect, k = 1) => addMod(mods, x.key, DISCRETE_KEYS.has(x.key) || x.key === 'matStart' || x.key === 'matIncome' ? x.value : x.value * mult * k);
  const lines = skillLines(charId);
  if (lines) {
    // 系統：Lv の能力＋開いた技・極み（すべて）。術を覚える・術の威力・奥義は系統 Lv で
    const lv = lineLevels(charId, ids);
    lines.forEach((l, i) => {
      for (const x of lineEffects(l, lv[i])) add(x);
      for (const id of lineOpenWaza(l, lv[i])) {
        const s = SKILL_BY_ID[id];
        if (!s) continue;
        for (const x of s.effects) add(x);
        flags.push(...s.flags.filter((f) => !f.startsWith('jutsu:') && !f.startsWith('jpow:')));
      }
      const j = JUTSU.find((x) => x.charId === charId && x.col === l.col && x.kind !== 'strike');
      if (j && lv[i] > 0) flags.push(`jpow:${j.id}=${Math.round(0.04 * lv[i] * 100) / 100}`);
      // 系統 Lv7 でその系統の術（基本の術も）の奥義を覚える
      const oj = JUTSU.find((x) => x.charId === charId && x.col === l.col);
      if (oj && l.col >= 0 && lv[i] >= LINE_OUGI_LV && !p.skills?.[charId]?.noOugi) flags.push(`jougi:${oj.id}`);
    });
    return { mods, flags };
  }
  for (const id of ids) {
    const s = SKILL_BY_ID[id];
    if (!s) continue;
    for (const x of s.effects) addMod(mods, x.key, DISCRETE_KEYS.has(x.key) || x.key === 'matStart' || x.key === 'matIncome' ? x.value : x.value * mult);
    flags.push(...s.flags);
  }
  return { mods, flags };
}

/** 画面向け：キャラ1人分の状態 */
export function skillView(p: Profile, charId: CharId) {
  const equipped = equippedSkills(p, charId);
  const cap = skillCapacity(p);
  return {
    charId,
    auto: p.skills?.[charId]?.auto ?? true,
    equipped,
    used: costOf(skillLines(charId) ? equipped.filter(isLineToken) : equipped),
    capacity: skillLines(charId) ? linePointsOf(p) : cap.total,
    presets: (p.skills?.[charId]?.presets ?? [null, null]).slice(0, CONFIG.skills.presetSlots),
    /** 3つの術（覚えたか・持っていくか） */
    jutsu: jutsuView(p, charId),
  };
}


// ---------------- 術（data/jutsu.ts） ----------------

/** 覚えている術（基本の術と、装着中のスキルで覚えた術） */
export function learnedJutsu(p: Profile, charId: CharId, ids: readonly string[] = equippedSkills(p, charId)): string[] {
  if (skillLines(charId)) return lineLearned(charId, lineLevels(charId, ids));
  const out = [defaultJutsu(charId).id];
  // 段では術の奥義を取ると、その術も覚える（同じ行の術を覚えるスキルを選ばなくても奥義が使えるように）
  const tags = skillLadder(charId) ? ['jutsu:', 'jougi:'] : ['jutsu:'];
  for (const id of ids) for (const f of SKILL_BY_ID[id]?.flags ?? []) for (const t of tags) if (f.startsWith(t) && JUTSU_BY_ID[f.slice(t.length)]?.charId === charId) out.push(f.slice(t.length));
  return [...new Set(out)];
}
/** 持っていく術（選んだ術を覚えていなければ基本の術） */
export function selectedJutsu(p: Profile, charId: CharId): string {
  const want = p.skills?.[charId]?.jutsu;
  return want && learnedJutsu(p, charId).includes(want) ? want : defaultJutsu(charId).id;
}
/** 持っていく術を選ぶ（覚えていない術は選べない。覚える前に選んでおくことはできない） */
export function setJutsu(p: Profile, charId: CharId, id: string): OpResult<{ jutsu: string }> {
  const j = JUTSU_BY_ID[id];
  if (!j || j.charId !== charId) return { ok: false, error: 'jutsu' };
  if (!learnedJutsu(p, charId).includes(id)) return { ok: false, error: 'locked' };
  ensure(p, charId).jutsu = id;
  return { ok: true, jutsu: id };
}
/** 画面向け：キャラの3つの術（覚えたか・選んでいるか・覚えるスキル） */
export function jutsuView(p: Profile, charId: CharId) {
  const learned = learnedJutsu(p, charId);
  const sel = selectedJutsu(p, charId);
  return JUTSU.filter((j) => j.charId === charId)
    .sort((a, b) => a.col - b.col)
    .map((j) => ({
      id: j.id,
      name: j.name,
      desc: j.desc,
      ougiDesc: j.ougiDesc,
      kind: j.kind,
      learned: learned.includes(j.id),
      selected: j.id === sel,
      /** 覚えるスキル（基本の術・系統で覚えるキャラは null） */
      skill: skillLines(charId) ? null : (SKILLS.find((s) => s.flags.includes(`jutsu:${j.id}`))?.id ?? null),
      /** 系統で覚えるキャラ：覚える条件（「普請Lv3」） */
      how: (() => { const l = skillLines(charId)?.find((x) => x.col === j.col); return l && j.kind !== 'strike' ? `${l.name}Lv${LINE_JUTSU_LV}` : null; })(),
    }));
}
